import {
  CreateIssueRelationshipBodyDto,
  GetIssueResponseDto,
  useApi,
} from '@issuemanager/api-client';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UseAddRelationshipParams {
  issueCode: string;
  onError?: (error: ExtendedAxiosError) => void;
  onSuccess?: (body: CreateIssueRelationshipBodyDto) => void;
}

export function useAddRelationship(
  params: UseAddRelationshipParams,
): ReturnType<
  typeof useMutation<
    GetIssueResponseDto,
    ExtendedAxiosError,
    CreateIssueRelationshipBodyDto
  >
> {
  const { issueCode, onError, onSuccess } = params;
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    GetIssueResponseDto,
    ExtendedAxiosError,
    CreateIssueRelationshipBodyDto
  >({
    mutationKey: ['add-relationship', issueCode],
    mutationFn: async (values: CreateIssueRelationshipBodyDto) => {
      const response = await api.issuesApi.createIssueRelationship(
        issueCode,
        values,
      );
      return response.data;
    },
    onError,
    onSuccess: async (issueData, body) => {
      queryClient.setQueryData<GetIssueResponseDto>(
        ['issues', issueCode],
        issueData,
      );

      await queryClient.invalidateQueries({
        queryKey: ['issue-relationships', issueCode],
      });

      onSuccess?.(body);
    },
  });
}
